import React, { useContext } from "react"
import merge from "deepmerge"

const defaultTheme = {
  button: {
    backgroundColor: "#1e1e1e",
    color: "#ffffff",
    borderRadius: 4,
    roundedCornerOption: "small",
    size: "medium",
    alignment: "center",
    margin: 0,
    width: "auto",
    href: ""
  },
  content: {
    backgroundColor: "#ffffff"
  }
}

export const themeFromTemplateSettings = settings => {
  return {
    button: {
      backgroundColor: settings["defaults.buttons.backgroundColor"],
      color: settings["defaults.buttons.color"],
      borderRadius: settings["defaults.buttons.borderRadius"]
    }
  }
}

const TemplateThemeContext = React.createContext(defaultTheme)

const InheritedThemeContext = React.createContext({ find: () => {} })

export function ThemeProvider({ theme, children }) {
  return (
    <TemplateThemeContext.Provider value={{ theme }}>
      {children}
    </TemplateThemeContext.Provider>
  )
}

export function InheritedThemeProvider({ theme, children }) {
  const ancestorContext = useContext(InheritedThemeContext)

  /**
   * Recursively climbs each ThemeProvider in the DOM to find the nearest theme
   * value matching the given `element` and `property`.
   *
   * If a style can't be found, this returns `undefined`, which is safe to apply
   * to a component's `style` prop. (React removes `undefined` styles when
   * rendering.)
   *
   * ```js
   * <ThemeProvider value={{ paragraph: { color: "blue" } }}>
   *   <ThemeProvider value={{ paragraph: { color: "red" } }}>
   *     <ThemeProvider={{ paragraph: { fontFamily: "Avenir" } }}>
   *       inheritedThemeContext.find("paragraph", "color") // "red"
   *     </ThemeProvider>
   *   </ThemeProvider>
   * </ThemeProvider>
   * ```
   *
   * @param {String} element
   * @param {String} property
   * @returns {String|Number|undefined} an inline style
   */
  function find(element, property) {
    const valueInCurrentContext =
      theme && theme[element] && theme[element][property]

    // The number 0 can be a valid value for a style (like with `lineHeight`).
    // In that case, we want to return it, not traverse the ancestor context.
    if (valueInCurrentContext === 0) {
      return valueInCurrentContext
    }

    return valueInCurrentContext || ancestorContext?.find(element, property)
  }

  return (
    <InheritedThemeContext.Provider value={{ find }}>
      {children}
    </InheritedThemeContext.Provider>
  )
}

export default function useTheme() {
  const { theme } = useContext(TemplateThemeContext)

  return merge(defaultTheme, theme || {})
}

/**
 * Returns a theme object with a new value on the specified element and
 * property. This is purely functional and does not dispatch an update.
 *
 * ```js
 * const newTheme = updateTheme(currentTheme, "paragraph", "color", "red")
 * update({ theme: newTheme })
 * ```
 *
 * @param {Object} theme
 * @param {String} element
 * @param {String} property
 * @param {String|Number} value
 * @returns {Object} theme
 */
export function createTheme(theme, element, property, value) {
  const result = theme || {}
  result[element] ||= {}
  result[element][property] = value
  return result
}

/**
 * Shorthand for accessing the `InheritedThemeContext`. For more information,
 * see `ThemeProvider`.
 *
 * @returns {Object} InheritedThemeContext
 */
export function useInheritedTheme() {
  return useContext(InheritedThemeContext)
}
